import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Text } from '../text';
import ProveedorCard from './ProveedorCard';

// Slice the list by what a proveedor owes you today. `pendientes` is the union
// (anything without a fianza emitida); porEmitir and futuras are its two
// disjoint halves, so they are offered separately.
const FILTROS = [
  { key: 'todos', label: 'Todos' },
  { key: 'pendientes', label: 'Con pendientes', test: (p) => p.counts.pendiente > 0 },
  { key: 'porEmitir', label: 'Por emitir', test: (p) => p.counts.porEmitir > 0 },
  { key: 'atrasadas', label: 'Atrasadas', test: (p) => p.counts.overdue > 0 },
  { key: 'futuras', label: 'Futuras', test: (p) => p.counts.futura > 0 },
  { key: 'vencidas', label: 'Vencidas', test: (p) => p.counts.vencida > 0 },
  { key: 'alCorriente', label: 'Al corriente', test: (p) => p.counts.pendiente === 0 && p.counts.vencida === 0 },
];

const ProveedoresChaseList = ({
  proveedoresAgg,
  mode = 'client',
  onContractClick,
  onAction,
  expandKey = null,
}) => {
  const [search, setSearch] = useState('');
  const [filtro, setFiltro] = useState('todos');

  // Deep links (Resumen click, reportes) name one proveedor via expandKey —
  // besides pre-expanding its card, bring it into view.
  const expandRef = useRef(null);
  useEffect(() => {
    if (expandKey && expandRef.current) {
      expandRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [expandKey]);

  const filtered = useMemo(() => {
    const activo = FILTROS.find((f) => f.key === filtro);
    const base = activo?.test ? proveedoresAgg.filter(activo.test) : proveedoresAgg;
    const term = search.trim().toLowerCase();
    if (!term) return base;
    return base.filter((p) => {
      if ((p.label || '').toLowerCase().includes(term)) return true;
      if ((p.rfc || '').toLowerCase().includes(term)) return true;
      const inPendientes = p.pendientes.some((fr) =>
        (fr.contractNumero || '').toLowerCase().includes(term) ||
        (fr.contractTitle || '').toLowerCase().includes(term) ||
        (fr.proyectoNombre || '').toLowerCase().includes(term) ||
        (fr.tipo || '').toLowerCase().includes(term)
      );
      if (inPendientes) return true;
      const inEmitidas = p.emitidas.some((f) =>
        (f.contractNumero || '').toLowerCase().includes(term) ||
        (f.contractTitle || '').toLowerCase().includes(term) ||
        (f.proyectoNombre || '').toLowerCase().includes(term) ||
        (f.tipoFianza || f.tipo || '').toLowerCase().includes(term)
      );
      return inEmitidas;
    });
  }, [proveedoresAgg, search, filtro]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <Text className="font-semibold text-azul-surelia">
          Proveedores ({proveedoresAgg.length})
        </Text>
        <input
          type="search"
          placeholder="Buscar proveedor, contrato, proyecto o tipo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-1.5 text-sm border border-azul-surelia/30 rounded-md focus:outline-none focus:ring-2 focus:ring-azul-surelia/20 focus:border-azul-surelia min-w-[260px]"
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {FILTROS.map((f) => {
          const n = f.test ? proveedoresAgg.filter(f.test).length : proveedoresAgg.length;
          const activo = filtro === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFiltro(f.key)}
              disabled={n === 0 && !activo}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                activo
                  ? 'bg-azul-surelia text-white border-azul-surelia'
                  : n === 0
                  ? 'bg-white text-zinc-300 border-zinc-200 cursor-not-allowed'
                  : 'bg-white text-zinc-600 border-azul-surelia/30 hover:border-azul-surelia'
              }`}
            >
              {f.label}
              <span className={`ml-1 tabular-nums ${activo ? 'text-white/70' : 'text-zinc-400'}`}>{n}</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-azul-surelia/15">
          <Text className="text-gray-600">
            {search
              ? `Sin resultados para "${search}".`
              : filtro !== 'todos'
              ? 'Ningún proveedor coincide con este filtro.'
              : 'No hay proveedores con fianzas en este convenio.'}
          </Text>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((p) => (
            <div key={p.key} ref={expandKey === p.key ? expandRef : null}>
              <ProveedorCard
                data={p}
                mode={mode}
                defaultExpanded={expandKey === p.key || filtered.length <= 3}
                onContractClick={onContractClick}
                onAction={onAction}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProveedoresChaseList;
